import * as userContentsApi from '@apis/admin-user-contents';
import { UserContent } from '@models/user-contents';
import { useQuery } from '@tanstack/react-query';
import { cdnImageUrl } from '@utils/image';
import { Button, Checkbox, Input, Modal, Select, Table } from 'antd';
import { ColumnsType } from 'antd/es/table';
import moment from 'moment';
import React, { useEffect, useState } from 'react';

const BOARD_LABELS: { [key: string]: string } = {
  review: '후기',
  curation: '콘텐츠발견',
  'partner-news': '선생님소식',
  vote: '투표',
};

const toPlainText = (value?: string) => {
  if (!value) {
    return '';
  }
  return value
    .replace(/<[^>]*>/g, ' ')
    .replace(/&nbsp;/gi, ' ')
    .replace(/\s+/g, ' ')
    .trim();
};

interface SelectUserContentModalProps {
  open?: boolean;
  onClose?: () => void;
  onAddUserContents?: (contents: UserContent[]) => void;
  selectLimit?: number;
  board?: string;
}

export const SelectUserContentModal: React.FC<SelectUserContentModalProps> = (props) => {
  const [searchKeyword, setSearchKeyword] = useState<string>('');
  const [board, setBoard] = useState<string>(props.board || 'all');
  const [query, setQuery] = useState<{ page: number; size: number; keyword?: string; board?: string }>({
    page: 0,
    size: 20,
    board: props.board && props.board !== 'all' ? props.board : undefined,
  });
  const [selectedContents, setSelectedContents] = useState<UserContent[]>([]);

  const userContentsQuery = useQuery({
    queryKey: ['admin-select-user-contents', JSON.stringify(query), props.open],
    queryFn: () =>
      userContentsApi.searchUserContents({
        ...query,
        board: query.board && query.board !== 'all' ? query.board : undefined,
      }),
    enabled: !!props.open,
  });

  useEffect(() => {
    if (!props.open) {
      setSelectedContents([]);
      setSearchKeyword('');
      setBoard(props.board || 'all');
      setQuery({
        page: 0,
        size: 20,
        board: props.board && props.board !== 'all' ? props.board : undefined,
      });
    }
  }, [props.open, props.board]);

  const contents = userContentsQuery.data?.data?.contents || [];
  const total = userContentsQuery.data?.data?.total || 0;
  const currentPage = (userContentsQuery.data?.data?.page ?? query.page) + 1;
  const pageSize = userContentsQuery.data?.data?.size ?? query.size;

  const toggleSelect = (record: UserContent, checked: boolean) => {
    setSelectedContents((prev) => {
      if (checked) {
        if (props.selectLimit === 1) {
          return [record];
        }
        if (prev.find((item) => item._id === record._id)) {
          return prev;
        }
        if (props.selectLimit && prev.length >= props.selectLimit) {
          return prev;
        }
        return [...prev, record];
      }
      return prev.filter((item) => item._id !== record._id);
    });
  };

  const TABLE_COLUMNS: ColumnsType<UserContent> = [
    {
      title: '',
      width: 56,
      render: (record) => (
        <Checkbox
          checked={!!selectedContents.find((item) => item._id === record._id)}
          disabled={
            !!props.selectLimit &&
            selectedContents.length >= props.selectLimit &&
            !selectedContents.find((item) => item._id === record._id)
          }
          onChange={(e) => {
            toggleSelect(record, e.target.checked);
          }}
        />
      ),
    },
    {
      title: 'ID',
      dataIndex: '_id',
      width: 220,
      render: (_id: string) => <div style={{ fontFamily: 'monospace', fontSize: '12px' }}>{_id}</div>,
    },
    {
      title: '구분',
      width: 100,
      render: (record) => BOARD_LABELS[record.board] || record.board || '-',
    },
    {
      title: '대표이미지',
      width: 88,
      render: (record) => {
        const thumbnail = record.images?.[0];
        if (!thumbnail) {
          return <div style={{ color: '#999', fontSize: '12px' }}>-</div>;
        }
        return (
          <img
            src={cdnImageUrl(thumbnail)}
            alt=""
            style={{ width: '52px', height: '52px', borderRadius: '6px', objectFit: 'cover', border: '1px solid #eee' }}
          />
        );
      },
    },
    {
      title: '작성자',
      width: 120,
      render: (record) => (
        <div style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {record.publisher?.nickname || record.publisher?.name || record.publisher?.profile?.name || '-'}
        </div>
      ),
    },
    {
      title: '제목/본문',
      width: 280,
      render: (record) => {
        const title = toPlainText(record.title) || '-';
        const text = toPlainText(record.text) || '-';
        return (
          <div style={{ maxWidth: '280px' }}>
            <div style={{ fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {title}
            </div>
            <div
              style={{
                fontSize: '12px',
                color: '#666',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}>
              {text}
            </div>
          </div>
        );
      },
    },
    {
      title: '작성일',
      width: 120,
      render: (record) => moment(record.createdAt).format('YYYY.MM.DD HH:mm'),
    },
    {
      title: '선택',
      width: 80,
      render: (record) => (
        <Button
          onClick={() => {
            props.onAddUserContents?.([record]);
            props.onClose?.();
          }}>
          선택
        </Button>
      ),
    },
  ];

  return (
    <Modal
      open={props.open}
      footer={false}
      onCancel={props.onClose}
      width={1200}
      zIndex={1500}
      styles={{ body: { maxHeight: '70vh', overflow: 'auto' } }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: '12px', marginBottom: '12px' }}>
        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
          <Select
            style={{ width: 160 }}
            value={board}
            onChange={(value) => {
              setBoard(value);
              setQuery((prev) => ({
                ...prev,
                page: 0,
                board: value === 'all' ? undefined : value,
              }));
              setSelectedContents([]);
            }}
            options={[
              { value: 'all', label: '구분 전체' },
              { value: 'curation', label: '콘텐츠발견' },
              { value: 'review', label: '후기' },
              { value: 'partner-news', label: '선생님소식' },
              { value: 'vote', label: '투표' },
            ]}
          />
          <Input
            style={{ width: '280px' }}
            placeholder="제목/본문/작성자 검색 후 엔터"
            value={searchKeyword}
            onChange={(e) => setSearchKeyword(e.target.value)}
            onKeyUp={(e) => {
              if (e.code === 'Enter') {
                setQuery((prev) => ({ ...prev, page: 0, keyword: searchKeyword || undefined }));
                setSelectedContents([]);
              }
            }}
          />
          <Button
            onClick={() => {
              setSearchKeyword('');
              setBoard(props.board || 'all');
              setQuery({
                page: 0,
                size: 20,
                board: props.board && props.board !== 'all' ? props.board : undefined,
              });
              setSelectedContents([]);
            }}>
            검색 초기화
          </Button>
          <div style={{ fontWeight: 'bold', whiteSpace: 'nowrap' }}>검색결과: {total}건</div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <div>{`${selectedContents.length}개 선택됨`}</div>
          <Button
            type="primary"
            disabled={selectedContents.length === 0}
            onClick={() => {
              props.onAddUserContents?.(selectedContents);
              props.onClose?.();
            }}>
            선택하기
          </Button>
        </div>
      </div>
      <Table
        rowKey={(record) => record._id}
        columns={TABLE_COLUMNS}
        dataSource={contents.filter((item) => item?._id)}
        loading={userContentsQuery.isLoading}
        tableLayout="fixed"
        pagination={{
          current: currentPage,
          pageSize: pageSize,
          total,
        }}
        onChange={(pagination) => {
          setQuery((prev) => ({
            ...prev,
            page: (pagination.current || 1) - 1,
            size: pagination.pageSize || prev.size,
          }));
        }}
      />
    </Modal>
  );
};

export default SelectUserContentModal;
